import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

// ---------- useProjetos ----------
export function useProjetos() {
  const { usuario } = useAuth();
  const [projetos, setProjetos] = useState([]);
  const [carregando, setCarregando] = useState(true);

  // Migração única: projetos sem ownerUid são atribuídos ao usuário atual
  useEffect(() => {
    if (!usuario?.uid) return;
    const migrar = async () => {
      const snap = await getDocs(
        query(collection(db, 'projetos'), where('ownerUid', '==', null))
      );
      if (snap.empty) return;
      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        batch.update(d.ref, {
          ownerUid: usuario.uid,
          ownerNome: usuario.nome || usuario.email,
          membroUids: [],
        });
      });
      await batch.commit();
    };
    migrar();
  }, [usuario?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!usuario?.uid) return;
    const uid = usuario.uid;

    let mapaDono = new Map();
    let mapaMembro = new Map();
    let loadDono = false;
    let loadMembro = false;

    const merge = () => {
      if (!loadDono || !loadMembro) return;
      const combined = new Map([...mapaDono, ...mapaMembro]);
      const lista = Array.from(combined.values()).sort((a, b) => {
        const aT = a.criadoEm?.seconds ?? 0;
        const bT = b.criadoEm?.seconds ?? 0;
        return bT - aT;
      });
      setProjetos(lista);
      setCarregando(false);
    };

    const unsub1 = onSnapshot(
      query(collection(db, 'projetos'), where('ownerUid', '==', uid)),
      (snap) => {
        mapaDono = new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
        loadDono = true;
        merge();
      }
    );

    const unsub2 = onSnapshot(
      query(collection(db, 'projetos'), where('membroUids', 'array-contains', uid)),
      (snap) => {
        mapaMembro = new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]));
        loadMembro = true;
        merge();
      }
    );

    return () => { unsub1(); unsub2(); };
  }, [usuario?.uid]);

  const criarProjeto = async (dados) => {
    await addDoc(collection(db, 'projetos'), {
      ...dados,
      ownerUid: usuario.uid,
      ownerNome: usuario.nome || usuario.email,
      membroUids: [],
      percentual: 0,
      totalTarefas: 0,
      tarefasConcluidas: 0,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
  };

  const atualizarProjeto = async (projetoId, dados) => {
    await updateDoc(doc(db, 'projetos', projetoId), {
      ...dados,
      atualizadoEm: serverTimestamp(),
    });
  };

  const excluirProjeto = async (projetoId) => {
    await deleteDoc(doc(db, 'projetos', projetoId));
  };

  return { projetos, carregando, criarProjeto, atualizarProjeto, excluirProjeto };
}

// ---------- useEtapas ----------
export function useEtapas(projetoId) {
  const [etapas, setEtapas] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!projetoId) return undefined;

    const refEtapas = collection(db, 'projetos', projetoId, 'etapas');
    const consulta = query(refEtapas, where('ordem', '>=', 0));

    const unsubscribe = onSnapshot(consulta, async (snapshot) => {
      const listaEtapas = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

      setEtapas(listaEtapas);
      setCarregando(false);

      const percentualProjeto = listaEtapas.length
        ? Math.round(
            listaEtapas.reduce((soma, etapa) => soma + (etapa.percentual || 0), 0) /
              listaEtapas.length
          )
        : 0;
      const totalTarefas = listaEtapas.reduce((s, e) => s + (e.totalTarefas || 0), 0);
      const tarefasConcluidas = listaEtapas.reduce((s, e) => s + (e.tarefasConcluidas || 0), 0);

      await updateDoc(doc(db, 'projetos', projetoId), {
        percentual: percentualProjeto,
        totalTarefas,
        tarefasConcluidas,
      });
    });

    return unsubscribe;
  }, [projetoId]);

  const criarEtapa = async (dados) => {
    await addDoc(collection(db, 'projetos', projetoId, 'etapas'), {
      ...dados,
      ordem: etapas.length + 1,
      status: 'aguardando',
      percentual: 0,
      totalTarefas: 0,
      tarefasConcluidas: 0,
      dataConclusao: null,
      criadoEm: serverTimestamp(),
    });
  };

  const atualizarEtapa = async (etapaId, dados) => {
    await updateDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId), dados);
  };

  const excluirEtapa = async (etapaId) => {
    await deleteDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId));
  };

  return { etapas, carregando, criarEtapa, atualizarEtapa, excluirEtapa };
}

// ---------- useTarefas ----------
export function useTarefas(projetoId, etapaId) {
  const { usuario } = useAuth();
  const [tarefas, setTarefas] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!projetoId || !etapaId) return undefined;

    const refTarefas = collection(db, 'projetos', projetoId, 'etapas', etapaId, 'tarefas');
    const consulta = query(refTarefas, where('criadoEm', '>=', new Date(0)));

    const unsubscribe = onSnapshot(consulta, async (snapshot) => {
      const listaTarefas = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.criadoEm?.seconds ?? 0) - (b.criadoEm?.seconds ?? 0));

      setTarefas(listaTarefas);
      setCarregando(false);

      const total = listaTarefas.length;
      const concluidas = listaTarefas.filter((t) => t.concluida).length;
      const percentual = total > 0 ? Math.round((concluidas / total) * 100) : 0;

      let status = 'aguardando';
      if (percentual === 100 && total > 0) status = 'concluida';
      else if (percentual > 0) status = 'em_andamento';

      await updateDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId), {
        percentual,
        totalTarefas: total,
        tarefasConcluidas: concluidas,
        status,
        dataConclusao: status === 'concluida' ? serverTimestamp() : null,
      });
    });

    return unsubscribe;
  }, [projetoId, etapaId]);

  const criarTarefa = async (dados) => {
    await addDoc(collection(db, 'projetos', projetoId, 'etapas', etapaId, 'tarefas'), {
      ...dados,
      concluida: false,
      dataConclusao: null,
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp(),
    });
  };

  const alternarTarefa = async (tarefaId, concluidaAtual, meta = {}) => {
    const novaConcluida = !concluidaAtual;
    await updateDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId, 'tarefas', tarefaId), {
      concluida: novaConcluida,
      dataConclusao: novaConcluida ? serverTimestamp() : null,
      concluidaPorUid: novaConcluida ? (usuario?.uid ?? null) : null,
      concluidaPorNome: novaConcluida ? (usuario?.nome || usuario?.email || null) : null,
      atualizadoEm: serverTimestamp(),
    });
    // Escreve/remove registro no historico de nível raiz para visibilidade compartilhada
    const historicoRef = doc(db, 'historico', tarefaId);
    if (novaConcluida) {
      await setDoc(historicoRef, {
        projetoId,
        etapaId,
        tarefaId,
        nome: meta.nome ?? '',
        criadoEm: meta.criadoEm ?? null,
        concluidaEm: serverTimestamp(),
        concluidaPorUid: usuario?.uid ?? null,
        concluidaPorNome: usuario?.nome || usuario?.email || null,
      });
    } else {
      await deleteDoc(historicoRef);
    }
  };

  const atualizarTarefa = async (tarefaId, dados) => {
    await updateDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId, 'tarefas', tarefaId), {
      ...dados,
      atualizadoEm: serverTimestamp(),
    });
  };

  const excluirTarefa = async (tarefaId) => {
    await deleteDoc(doc(db, 'projetos', projetoId, 'etapas', etapaId, 'tarefas', tarefaId));
  };

  return { tarefas, carregando, criarTarefa, alternarTarefa, atualizarTarefa, excluirTarefa };
}

// ---------- useMembros ----------
// Retorna usuários que fazem parte dos projetos visíveis ao usuário atual
export function useMembros(uids) {
  const [membros, setMembros] = useState([]);

  useEffect(() => {
    const lista = [...new Set(uids)].filter(Boolean);
    if (lista.length === 0) { setMembros([]); return; }

    // Firestore limita "in" a 30 itens — mais que suficiente para equipes
    const q = query(collection(db, 'usuarios'), where(documentId(), 'in', lista.slice(0, 30)));
    const unsub = onSnapshot(q, (snap) => {
      setMembros(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
    });
    return unsub;
  }, [uids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  return membros;
}
